"use client";

import { useState, useEffect } from "react";
import { Row, Col, Form, FloatingLabel } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import * as myApi from "../../Api";
import { validateEmail } from "../../../utils/formValidation";

const SignInForm = () => {
  const [emailError, setEmailError] = useState('');
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(null); // null = loading, false = not passed, true = passed
  let navigate = useNavigate();

  useEffect(() => {
    const fetchDeadline = async () => {
      try {
        const response = await myApi.get('/deadline');
        if (response.data.result) {
          const deadlineInfo = response.data.result;
          const dateNow = new Date();
          const dateLimit = new Date(deadlineInfo.general_deadline);
          setIsDeadlinePassed(dateNow > dateLimit);
        }
      } catch (error) {
        console.error('Error fetching deadline:', error);
        // Default to not passed if there's an error
        setIsDeadlinePassed(false);
      }
    };

    fetchDeadline();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const email = formData.get("username");
    
    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      toast(emailValidation.message, { type: "error" });
      setEmailError(emailValidation.message);
      return;
    }
    setEmailError('');

    const data = {
      username: email,
      passwd: formData.get("passwd"),
    };
    try {
      const response = await myApi.post("/auth", data);

      if (response.data.result !== undefined) {
        // check if user_status is active
        if (response.data.result.user_status !== "active") {
          toast("User belum diaktivasi. Anda akan diarahkan ke halaman aktivasi.", { type: "warning" });
          setTimeout(() => {
            // DO NOT store token for inactive users
            // Only store minimal user info for activation page
            const inactiveUserData = {
              email: response.data.result.email,
              name: response.data.result.name,
              user_status: response.data.result.user_status
            };
            navigate("/auth/resend-activation/", { state: { data: inactiveUserData } });
          }, 1500)
        } else {
          // Success — set token and redirect
          myApi.setToken(response.data.result.token);
          myApi.setUser(response.data.result);
          setTimeout(() => {
            // Redirect based on user role
            // Admin -> /dashboard/
            // Peserta -> /paper/list/
            if (response.data.result.role === 'admin') {
              window.location.href = '/dashboard/';
            } else {
              window.location.href = '/paper/list/';
            }
          }, 1500)
        }
      } else {
        toast(response.data.message || 'Login failed', { type: "error" });
      }
    } catch (error) {
      toast(error.message || 'An error occurred', { type: "error" });
    }
  };

  return (
    <>
      <div className="auth-main-content m-auto m-1230 px-3">
        <Row className="align-items-center">
          <Col lg={6} className="d-none d-lg-block">
            <img
              src="/images/ejavec/side-02.jpg"
              className="rounded-3"
              alt="login"
              width={500}
              height={804}
            />
          </Col>

          <Col lg={6}>
            <div className="mw-480 ms-lg-auto">
              <div className="d-inline-block mb-4">
                <img
                  src="/images/ejavec/ejavec-logo.svg"
                  className="rounded-3 for-light-logo"
                  alt="login"
                  width={100}
                  height={26}
                />
                <img
                  src="/images/ejavec/ejavec-forum_sitelogo_2026.svg"
                  className="rounded-3 for-dark-logo"
                  alt="login"
                  width={100}
                  height={26}
                />
              </div>

              <h3 className="fs-28 mb-2">Welcome!</h3>
              <p className="fw-medium fs-16 mb-4">
                Sign In with your account
              </p>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <FloatingLabel
                    controlId="floatingInput"
                    label="Email Address"
                    className="mb-3"
                  >
                    <Form.Control
                      type="email"
                      className={`h-55 ${emailError ? 'is-invalid' : ''}`}
                      name="username"
                      placeholder="email@example.com"
                      required
                      onChange={(e) => {
                        if (emailError) {
                          const validation = validateEmail(e.target.value);
                          setEmailError(validation.valid ? '' : validation.message);
                        }
                      }}
                    />
                    {emailError && (
                      <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-4">
                  <FloatingLabel
                    controlId="floatingPassword"
                    label="Password"
                    className="mb-3"
                  >
                      <Form.Control
                      type="password"
                      className="h-55"
                      name="passwd"
                      placeholder="Type password"
                      required
                    />
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Link
                    to="/auth/forgot-password/"
                    className="fw-medium text-primary text-decoration-none"
                  >
                    Forgot Password?
                  </Link>
                </Form.Group>

                <Form.Group className="mb-4">
                  <button
                    type="submit"
                    className="btn btn-primary fw-medium py-2 px-3 w-100"
                  >
                    <div className="d-flex align-items-center justify-content-center py-1">
                      <span className="material-symbols-outlined fs-20 text-white me-2">
                        login
                      </span>
                      <span>Sign In</span>
                    </div>
                  </button>
                </Form.Group>

                <Form.Group>
                  <p>
                    {isDeadlinePassed === null ? (
                      <span>Loading...</span>
                    ) : isDeadlinePassed ? (
                      <span className="text-danger">
                        <span className="material-symbols-outlined fs-20 text-danger me-2">
                          info
                        </span><span>Paper Submission registration is already closed.</span>
                      </span>
                    ) : (
                      <span>
                        Don’t have an account.{" "}
                        <Link
                          to="/auth/sign-up/"
                          className="fw-medium text-primary text-decoration-none"
                        >
                          Sign Up
                        </Link>
                      </span>
                    )}
                  </p>
                </Form.Group>
              </Form>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SignInForm;
